import * as React from "react";
import { Line, ResponsiveLine, Serie } from '@nivo/line'
import { roundToBottom, roundToTop, createRange } from "../../misc/math-functions";

interface FlowVolumeGraphProps {
    data: Serie[];
    minFlow: number;
    maxFlow: number;
    minVolume: number;
    maxVolume: number;
}

function scaleGraph(minFlow, maxFlow, minVolume, maxVolume) {
    const minV = roundToBottom(minVolume);
    const maxV = roundToTop(maxVolume);

    const minF = roundToBottom(minFlow);
    const maxF = roundToTop(maxFlow);

    let yRange = maxF - minF;

    let xRange = maxV - minV;

    while (yRange < xRange * 2) {
        yRange++;
    }
    //yRange is xRange * 2 so should be even
    if (yRange % 2 === 1) {
        yRange++;
    }

    //To guarantee 2:1 aspect ratio, there should be the same 2:1 ratio of ticks as the axis lengths are fixed.
    while (xRange * 2 < yRange) {
        xRange++;
    }

    const maxX = minV + xRange;
    //+1 as range is not inclusive of end value.
    const xTicks = createRange(minV, maxX + 1, 1);

    const maxY = minF + yRange;
    const yTicks = createRange(minF, maxY + 1, 1);

    return {
        minX: minV,
        maxX,
        minY: minF,
        maxY,
        xTicks,
        yTicks
    }
}

export const FlowVolumeGraph = ({ data, minFlow, maxFlow, minVolume, maxVolume }: FlowVolumeGraphProps) => {   
    const {
        minX,
        maxX,
        minY,
        maxY,
        xTicks,
        yTicks
    } = scaleGraph(minFlow, maxFlow, minVolume, maxVolume);

    return (<ResponsiveLine
        data={data}
        curve="basis"
        margin={{ top: 30, right: 215, bottom: 50, left: 160 }}
        xScale={{ type: 'linear', min: minX, max: maxX }}
        yScale={{ type: 'linear', min: minY, max: maxY }}
        colors={d => d.color}
        animate={false}
        axisTop={null}
        axisRight={null}
        axisBottom={{
            tickValues: xTicks,
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Volume',
            legendOffset: 30,
            legendPosition: 'middle'
        }}
        axisLeft={{
            tickValues: yTicks,
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Flow',
            legendOffset: -40,
            legendPosition: 'middle'
        }}
        lineWidth={0.5}
        enablePoints={false}
        enablePointLabel={false}
        isInteractive={true}
        useMesh={true}
        enableCrosshair={true}
        tooltip={({ point }) => {
            return <div className="lineChartTooltip">
                <span className="lineChartTooltipIcon" style={{ background: point.color }}></span>
                <span>Volume: <strong>{point.data.x as number}</strong> Flow: <strong>{point.data.y as number}</strong></span>
            </div>
        }}
        gridXValues={xTicks}
        gridYValues={yTicks}
        legends={[
            {
                anchor: 'top',
                direction: 'row',
                justify: false,
                translateX: 10,
                translateY: -20,
                itemsSpacing: 2,
                itemDirection: 'left-to-right',
                itemWidth: 80,
                itemHeight: 12,
                itemOpacity: 0.75,
                symbolSize: 12,
                symbolShape: 'square',
                symbolBorderColor: 'rgba(0, 0, 0, .5)',
            }
        ]}
        markers={[
            {
                value: 0,
                axis: 'y',
                lineStyle: {
                    stroke: 'black'
                }
            },
            {
                value: 0,
                axis: 'x',
                lineStyle: {
                    stroke: 'black'
                }
            }
        ]}
    />);
}
